import React, { useEffect, useState } from 'react';
import {
  Title,
  Group,
  Button,
  TextInput,
  Textarea,
  Select,
  Stack,
  Grid,
  Card,
  ActionIcon,
  LoadingOverlay,
  Alert,
  Text,
  MultiSelect,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconUser,
  IconSchool,
  IconCalendar,
  IconMedicalCross,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useStudent, useCreateStudent, useUpdateStudent } from '../../hooks/useStudents';
import { useSchools } from '../../hooks/useSchools';

interface StudentFormData {
  first_name: string;
  last_name: string;
  nickname?: string;
  date_of_birth: Date | null;
  gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  school_id: string | null;
  class_id?: string | null;
  parent_ids: string[];
  status: 'active' | 'inactive';
  enrollment_date: Date | null;
  medical_notes?: string;
  emergency_contact?: {
    name: string;
    relationship: string;
    phone: string;
    email?: string;
  };
}

const StudentForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = id !== 'new' && !!id;
  
  const [availableParents, setAvailableParents] = useState<any[]>([]);

  const {
    data: student,
    isLoading: studentLoading,
    error: studentError,
  } = useStudent(id || '', isEditing ? undefined : '');

  const { data: schoolsData } = useSchools({ per_page: 100 });

  const createStudentMutation = useCreateStudent();
  const updateStudentMutation = useUpdateStudent();

  const form = useForm<StudentFormData>({
    initialValues: {
      first_name: '',
      last_name: '',
      nickname: '',
      date_of_birth: null,
      gender: 'male' as const,
      school_id: null,
      class_id: null,
      parent_ids: [],
      status: 'active' as const,
      enrollment_date: new Date(),
      medical_notes: '',
      emergency_contact: {
        name: '',
        relationship: '',
        phone: '',
        email: '',
      },
    },
    validate: {
      first_name: (value) => (!value.trim() ? 'First name is required' : null),
      last_name: (value) => (!value.trim() ? 'Last name is required' : null),
      date_of_birth: (value) => (!value ? 'Date of birth is required' : null),
      gender: (value) => (!value ? 'Gender is required' : null),
      school_id: (value) => (!value ? 'School is required' : null),
      enrollment_date: (value) => (!value ? 'Enrollment date is required' : null),
      emergency_contact: {
        name: (value) => (!value?.trim() ? 'Emergency contact name is required' : null),
        relationship: (value) => (!value?.trim() ? 'Relationship is required' : null),
        phone: (value) => {
          if (!value?.trim()) return 'Emergency contact phone is required';
          if (!/^[\+]?[0-9\-\s\(\)]+$/.test(value)) return 'Invalid phone format';
          return null;
        },
        email: (value) => {
          if (value && value.trim() && !/^\\S+@\\S+\\.\\S+$/.test(value)) {
            return 'Invalid email format';
          }
          return null;
        },
      },
    },
  });

  const schools = schoolsData?.schools || [];

  // Mock parent data - in real app, you'd fetch from API
  useEffect(() => {
    // Simulate fetching parent users
    setAvailableParents([
      { value: '1', label: 'John Doe (john@example.com)' },
      { value: '2', label: 'Jane Smith (jane@example.com)' },
      { value: '3', label: 'Mike Johnson (mike@example.com)' },
    ]);
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (isEditing && student) {
      form.setValues({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        nickname: student.nickname || '',
        date_of_birth: student.date_of_birth ? new Date(student.date_of_birth) : null,
        gender: student.gender || 'male',
        school_id: student.school_id || null,
        class_id: student.class_id || null,
        parent_ids: student.parent_ids || [],
        status: (student.status === 'graduated' ? 'active' : student.status) || 'active',
        enrollment_date: student.enrollment_date ? new Date(student.enrollment_date) : null,
        medical_notes: student.medical_notes || '',
        emergency_contact: student.emergency_contact || {
          name: '',
          relationship: '',
          phone: '',
          email: '',
        },
      });
    }
  }, [student, isEditing]);

  const handleSubmit = async (values: StudentFormData) => {
    try {
      const submitData = {
        ...values,
        date_of_birth: values.date_of_birth?.toISOString().split('T')[0] || '',
        enrollment_date: values.enrollment_date?.toISOString().split('T')[0] || '',
        parent_ids: values.parent_ids,
        school_id: values.school_id?.toString() || '0',
        class_id: values.class_id || undefined,
      };

      if (isEditing) {
        await updateStudentMutation.mutateAsync({
          id: id!,
          data: submitData,
        });
        notifications.show({
          title: 'Success',
          message: 'Student updated successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else {
        const response = await createStudentMutation.mutateAsync(submitData);
        notifications.show({
          title: 'Success',
          message: 'Student enrolled successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
        navigate(`/admin/students/${response.id}`);
        return;
      }
      
      navigate('/admin/students');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: isEditing ? 'Failed to update student' : 'Failed to enroll student',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      navigate(`/admin/students/${id}`);
    } else {
      navigate('/admin/students');
    }
  };

  const isLoading = createStudentMutation.isPending || updateStudentMutation.isPending;

  if (isEditing && studentError) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error loading student"
        color="red"
      >
        Failed to load student details. Please try again.
      </Alert>
    );
  }

  if (isEditing && studentLoading) {
    return <LoadingOverlay visible />;
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ActionIcon variant="subtle" onClick={handleCancel}>
              <IconArrowLeft size={16} />
            </ActionIcon>
            <div>
              <Title order={2}>
                {isEditing ? 'Edit Student' : 'Enroll New Student'}
              </Title>
              <Text size="sm" c="dimmed">
                {isEditing ? 'Update student information' : 'Add a new student to the system'}
              </Text>
            </div>
          </Group>
          <Group>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              leftSection={<IconCheck size={16} />}
            >
              {isEditing ? 'Update Student' : 'Enroll Student'}
            </Button>
          </Group>
        </Group>

        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              {/* Basic Information */}
              <Card>
                <Group mb="md">
                  <IconUser size={20} color="blue" />
                  <Title order={4}>Basic Information</Title>
                </Group>

                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="First Name"
                        placeholder="Enter first name"
                        required
                        {...form.getInputProps('first_name')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Last Name"
                        placeholder="Enter last name"
                        required
                        {...form.getInputProps('last_name')}
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Nickname (Optional)"
                        placeholder="Enter nickname"
                        {...form.getInputProps('nickname')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Gender"
                        placeholder="Select gender"
                        required
                        data={[
                          { value: 'male', label: 'Male' },
                          { value: 'female', label: 'Female' },
                          { value: 'other', label: 'Other' },
                        ]}
                        value={form.values.gender}
                        onChange={(value) => form.setFieldValue('gender', (value || 'prefer_not_to_say') as 'male' | 'female' | 'other' | 'prefer_not_to_say')}
                        error={form.errors.gender as string}
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <DateInput
                        label="Date of Birth"
                        placeholder="Select date of birth"
                        required
                        leftSection={<IconCalendar size={16} />}
                        {...form.getInputProps('date_of_birth')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <DateInput
                        label="Enrollment Date"
                        placeholder="Select enrollment date"
                        required
                        leftSection={<IconCalendar size={16} />}
                        {...form.getInputProps('enrollment_date')}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Card>

              {/* School Information */}
              <Card>
                <Group mb="md">
                  <IconSchool size={20} color="green" />
                  <Title order={4}>School Information</Title>
                </Group>

                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label="School"
                      placeholder="Select school"
                      required
                      data={schools
                        .filter((school: any) => school && school.name)
                        .map((school: any) => ({
                          value: school.id.toString(),
                          label: school.name,
                        }))}
                      value={form.values.school_id?.toString() || ''}
                      onChange={(value) => form.setFieldValue('school_id', value || null)}
                      error={form.errors.school_id as string}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label="Class (Optional)"
                      placeholder="Select class"
                      data={[
                        { value: '1', label: 'Kindergarten A' },
                        { value: '2', label: 'Kindergarten B' },
                        { value: '3', label: 'Pre-K' },
                      ]}
                      value={form.values.class_id?.toString() || ''}
                      onChange={(value) => form.setFieldValue('class_id', value || null)}
                      error={form.errors.class_id as string}
                    />
                  </Grid.Col>
                </Grid>

                <MultiSelect
                  label="Parents"
                  placeholder="Select parent accounts"
                  data={availableParents}
                  value={form.values.parent_ids}
                  onChange={(value) => form.setFieldValue('parent_ids', value)}
                  error={form.errors.parent_ids as string}
                />
              </Card>

              {/* Emergency Contact */}
              <Card>
                <Group mb="md">
                  <IconPhone size={20} color="red" />
                  <Title order={4}>Emergency Contact</Title>
                </Group>

                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Contact Name"
                        placeholder="Enter contact name"
                        required
                        {...form.getInputProps('emergency_contact.name')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Relationship"
                        placeholder="e.g., Mother, Father, Guardian"
                        required
                        {...form.getInputProps('emergency_contact.relationship')}
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Phone Number"
                        placeholder="+1 (555) 123-4567"
                        required
                        leftSection={<IconPhone size={16} />}
                        {...form.getInputProps('emergency_contact.phone')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Email (Optional)"
                        placeholder="contact@example.com"
                        leftSection={<IconMail size={16} />}
                        {...form.getInputProps('emergency_contact.email')}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Card>

              {/* Medical Information */}
              <Card>
                <Group mb="md">
                  <IconMedicalCross size={20} color="orange" />
                  <Title order={4}>Medical Information</Title>
                </Group>

                <Textarea
                  label="Medical Notes (Optional)"
                  placeholder="Any allergies, medical conditions, or special needs..."
                  autosize
                  minRows={3}
                  maxRows={6}
                  {...form.getInputProps('medical_notes')}
                />
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md">
              {/* Status & Settings */}
              <Card>
                <Title order={4} mb="md">Status & Settings</Title>
                
                <Select
                  label="Status"
                  description="Set student enrollment status"
                  data={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  value={form.values.status}
                  onChange={(value) => form.setFieldValue('status', (value || 'active') as 'active' | 'inactive')}
                  error={form.errors.status as string}
                />
              </Card>

              {/* Help Information */}
              <Card>
                <Title order={4} mb="md">Enrollment Guide</Title>
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    • All required fields must be completed
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Parent accounts can be linked later
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Emergency contact is required for safety
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Face recognition photos can be added after enrollment
                  </Text>
                </Stack>
              </Card>

              {isEditing && student && (
                <Card>
                  <Title order={4} mb="md">Student Information</Title>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Enrolled</Text>
                      <Text size="sm">
                        {new Date(student.created_at).toLocaleDateString()}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Last Updated</Text>
                      <Text size="sm">
                        {new Date(student.updated_at).toLocaleDateString()}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Photos</Text>
                      <Text size="sm">{student.photos_count}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Activities</Text>
                      <Text size="sm">{student.activities_count}</Text>
                    </Group>
                  </Stack>
                </Card>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </form>
  );
};

export default StudentForm;